/**
 * API utilities for billing and catalog operations
 */

import { API_BASE_URL } from '../config/api';
import {
  CatalogItem,
  CatalogItemCreate,
  CatalogItemUpdate,
  OrderLineItem,
  OrderLineItemCreate,
  OrderLineItemUpdate,
  EncounterOrderSummary,
  EncounterOrderTotals,
  BillableItemCategory,
  PriceHistoryResponse,
  PriceHistoryItem
} from '../types/billing';

/**
 * Get authentication headers for API requests
 */
function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('token') || localStorage.getItem('access_token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
  };
}

// ===== CATALOG ITEM OPERATIONS =====

/**
 * Get all catalog items for a practice
 */
export async function getCatalogItems(
  practiceId: string,
  category?: BillableItemCategory,
  search?: string,
  activeOnly: boolean = true,
  includeAllStandards: boolean = false
): Promise<CatalogItem[]> {
  const params = new URLSearchParams();
  if (category) params.append('category', category);
  if (search) params.append('search', search);
  params.append('active_only', activeOnly.toString());
  if (includeAllStandards) params.append('include_all_standards', 'true');

  const url = `${API_BASE_URL}/api/v1/practices/${practiceId}/catalog?${params.toString()}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to fetch catalog items' }));
    throw new Error(error.detail || 'Failed to fetch catalog items');
  }

  return response.json();
}

/**
 * Get catalog items by category
 */
export async function getCatalogItemsByCategory(
  practiceId: string,
  category: BillableItemCategory,
  activeOnly: boolean = true
): Promise<CatalogItem[]> {
  return getCatalogItems(practiceId, category, undefined, activeOnly);
}

/**
 * Create a custom catalog item for a practice
 */
export async function createCatalogItem(
  practiceId: string,
  data: CatalogItemCreate
): Promise<CatalogItem> {
  const response = await fetch(`${API_BASE_URL}/api/v1/practices/${practiceId}/catalog`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to create catalog item' }));
    throw new Error(error.detail || 'Failed to create catalog item');
  }

  return response.json();
}

/**
 * Update a catalog item
 */
export async function updateCatalogItem(
  practiceId: string,
  itemId: string,
  data: CatalogItemUpdate
): Promise<CatalogItem> {
  const response = await fetch(`${API_BASE_URL}/api/v1/practices/${practiceId}/catalog/${itemId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to update catalog item' }));
    throw new Error(error.detail || 'Failed to update catalog item');
  }

  return response.json();
}

/**
 * Delete (deactivate) a catalog item
 */
export async function deleteCatalogItem(
  practiceId: string,
  itemId: string
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/v1/practices/${practiceId}/catalog/${itemId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to delete catalog item' }));
    throw new Error(error.detail || 'Failed to delete catalog item');
  }
}

/**
 * Permanently delete a custom catalog item (hard delete)
 * Only works on custom items with no references
 */
export async function hardDeleteCatalogItem(
  practiceId: string,
  itemId: string
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/v1/practices/${practiceId}/catalog/${itemId}/hard`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to delete catalog item' }));
    throw new Error(error.detail || 'Failed to delete catalog item');
  }
}

// ===== ORDER LINE ITEM OPERATIONS =====

/**
 * Get all order line items for an encounter with totals
 */
export async function getEncounterOrders(encounterId: string): Promise<EncounterOrderSummary> {
  const response = await fetch(`${API_BASE_URL}/api/v1/encounters/${encounterId}/orders`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to fetch encounter orders' }));
    throw new Error(error.detail || 'Failed to fetch encounter orders');
  }

  return response.json();
}

/**
 * Add a line item to an encounter
 */
export async function addOrderLineItem(
  encounterId: string,
  data: OrderLineItemCreate
): Promise<OrderLineItem> {
  const response = await fetch(`${API_BASE_URL}/api/v1/encounters/${encounterId}/orders`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({ detail: 'Failed to add order item' }));
    const error: any = new Error(errorBody.detail || 'Failed to add order item');
    error.status = response.status;
    throw error;
  }

  return response.json();
}

/**
 * Update an order line item
 */
export async function updateOrderLineItem(
  encounterId: string,
  lineItemId: string,
  data: OrderLineItemUpdate
): Promise<OrderLineItem> {
  const response = await fetch(`${API_BASE_URL}/api/v1/encounters/${encounterId}/orders/${lineItemId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to update order item' }));
    throw new Error(error.detail || 'Failed to update order item');
  }

  return response.json();
}

/**
 * Delete an order line item
 */
export async function deleteOrderLineItem(
  encounterId: string,
  lineItemId: string
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/v1/encounters/${encounterId}/orders/${lineItemId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to delete order item' }));
    throw new Error(error.detail || 'Failed to delete order item');
  }
}

/**
 * Get calculated totals for an encounter
 */
export async function getEncounterTotal(encounterId: string): Promise<EncounterOrderTotals> {
  const response = await fetch(`${API_BASE_URL}/api/v1/encounters/${encounterId}/orders/total`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to fetch encounter total' }));
    throw new Error(error.detail || 'Failed to fetch encounter total');
  }

  return response.json();
}

/**
 * Bulk import all labs from diagnostic catalog to billing catalog
 */
export async function importLabsToCatalog(practiceId: string): Promise<{ created: number; skipped: number; total: number }> {
  const response = await fetch(`${API_BASE_URL}/api/v1/practices/${practiceId}/catalog/import-labs`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to import labs' }));
    throw new Error(error.detail || 'Failed to import labs');
  }

  return response.json();
}

/**
 * Bulk import all immunizations from immunization catalog to billing catalog
 */
export async function importImmunizationsToCatalog(practiceId: string): Promise<{ created: number; skipped: number; total: number }> {
  const response = await fetch(`${API_BASE_URL}/api/v1/practices/${practiceId}/catalog/import-immunizations`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to import immunizations' }));
    throw new Error(error.detail || 'Failed to import immunizations');
  }

  return response.json();
}

// ===== PRICE HISTORY OPERATIONS =====

/**
 * Get price history for a catalog item
 */
export async function getPriceHistory(
  practiceId: string,
  itemId: string
): Promise<PriceHistoryResponse> {
  const response = await fetch(`${API_BASE_URL}/api/v1/practices/${practiceId}/catalog/${itemId}/prices`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to fetch price history' }));
    throw new Error(error.detail || 'Failed to fetch price history');
  }

  return response.json();
}

/**
 * Add a new price for a catalog item (creates price history entry)
 */
export async function addNewPrice(
  practiceId: string,
  itemId: string,
  newPrice: number
): Promise<PriceHistoryItem> {
  const response = await fetch(`${API_BASE_URL}/api/v1/practices/${practiceId}/catalog/${itemId}/prices?new_price=${newPrice}`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to add new price' }));
    throw new Error(error.detail || 'Failed to add new price');
  }

  return response.json();
}

